import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@mui/styles';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Chip
} from '@mui/material';
import { Search, Dashboard, QueuePlayNext, QuestionAnswer, AccountBalance, AccessTimeFilled, GroupAdd, SpeakerGroup, IntegrationInstructions, SmartToy, Assignment, SettingsApplications } from '@mui/icons-material';
import AutomationIcon from '@mui/icons-material/Assistant';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { AbilityContext, Can } from '../../config/ability-context';
import Base from '../../components/layout-base/base';
import Api from '../../services/api';

const styles = (theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '20px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
    '& h3': {
      color: '#2c3e50',
      fontWeight: 600,
      marginBottom: '10px',
    },
    '& p': {
      color: '#7f8c8d',
      fontSize: '1.1rem',
    }
  },
  searchContainer: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'center',
  },
  searchField: {
    maxWidth: '500px',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      borderRadius: '25px',
      '& fieldset': {
        borderColor: '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: 'var(--mexx-green-primary, #8BC34A)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--mexx-green-primary, #8BC34A)',
      },
    },
  },
  contentContainer: {
    display: 'flex',
    gap: '20px',
    minHeight: '600px',
  },
  categoriesGrid: {
    flex: 1,
    '& .MuiGrid-item': {
      display: 'flex',
    }
  },
  categoryCard: {
    width: '100%',
    height: '180px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: 'white',
    border: '2px solid transparent',
    borderRadius: '15px',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      borderColor: 'var(--mexx-green-primary, #8BC34A)',
    },
    '&.selected': {
      borderColor: 'var(--mexx-green-primary, #8BC34A)',
      backgroundColor: '#f8fff8',
    }
  },
  categoryContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    textAlign: 'center',
  },
  categoryIcon: {
    fontSize: '3rem',
    color: 'var(--mexx-green-primary, #8BC34A)',
    marginBottom: '15px',
  },
  categoryTitle: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '1.1rem',
    marginBottom: '5px',
  },
  categorySubtitle: {
    color: '#7f8c8d',
    fontSize: '0.9rem',
  },
  rightPanel: {
    width: '380px',
    backgroundColor: 'white',
    borderRadius: '15px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
    padding: '0',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '20px',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fafafa',
  },
  panelTitle: {
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '10px',
  },
  panelSubtitle: {
    color: '#7f8c8d',
    fontSize: '0.9rem',
  },
  subitemsList: {
    padding: '0',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  subitemListItem: {
    padding: '15px 20px',
    borderBottom: '1px solid #f5f5f5',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8fff8',
      paddingLeft: '25px',
    },
    '&:last-child': {
      borderBottom: 'none',
    }
  },
  subitemIcon: {
    color: 'var(--mexx-green-primary, #8BC34A)',
    minWidth: '40px',
  },
  subitemText: {
    '& .MuiTypography-root': {
      fontSize: '0.95rem',
      color: '#2c3e50',
    }
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#7f8c8d',
  },
  itemCount: {
    marginLeft: 'auto',
    backgroundColor: 'var(--mexx-green-primary, #8BC34A)',
    color: 'white',
    fontSize: '0.8rem',
  }
});

class ModernMenu extends Component {
  static contextType = AbilityContext;

  constructor(props) {
    super(props);
    this.state = {
      searchTerm: '',
      selectedCategory: null,
      menuItems: [],
      filteredSubitems: [],
    };
  }

  componentDidMount() {
    this.buildMenuItems();
  }

  buildMenuItems = () => {
    const { userSession, intl } = this.props;
    const menuItems = [];

    if (!userSession?.user) return;

    // Build the same menu structure as the original
    this.isAdm = userSession.user.userrole?.some(role => role.name === 'Administrador');
    this.isClient = userSession.user.userrole?.some(role => role.name === 'Cliente');
    this.isAdmOrg = userSession.user.userrole?.some(role => role.name === 'Administrador da Organização');
    this.isAdmGlobal = userSession.user.userrole?.some(role => role.name === 'Administrador Global');
    this.isTec = userSession.user.userrole?.some(role => role.name === 'Técnico');

    // Dashboards
    menuItems.push({
      id: 'dashboards',
      name: intl.formatMessage({ id: 'dashboards' }),
      icon: Dashboard,
      subtitle: 'Visão geral e métricas',
      items: [
        {
          name: intl.formatMessage({ id: 'menu.dashboards.operation' }),
          link: `/${this.props.match.params.lang}/dashboard`,
          state: 'operationdashboard',
        }
      ]
    });

    // Chamados (if user can list)
    if (this.context.can('List', 'Called')) {
      const calledItems = [];

      let linkCalleds = `/${this.props.match.params.lang}/calleds`;
      if (userSession?.user?.enablenewscreencalled) {
        linkCalleds = `/${this.props.match.params.lang}/new-calleds`;
      }

      calledItems.push({
        name: intl.formatMessage({ id: 'called.header' }),
        link: linkCalleds,
        state: 'calleds',
      });

      if (this.context.can('List', 'AssignedCalled')) {
        calledItems.push({
          name: intl.formatMessage({ id: 'called.assing' }),
          link: `/${this.props.match.params.lang}/assignedcalleds`,
          state: 'assignedcalleds',
        });
      }

      menuItems.push({
        id: 'calleds',
        name: intl.formatMessage({ id: 'called.header' }),
        icon: QueuePlayNext,
        subtitle: 'Gestão de tickets e chamados',
        items: calledItems
      });
    }

    // Sistema 2Talk
    if ((this.isTec || this.isAdm || this.isAdmGlobal || this.isClient)) {
      const talkItems = [];

      if (!this.isClient) {
        talkItems.push({
          name: 'Conversas',
          link: `/${this.props.match.params.lang}/chat-attendance-list`,
          state: 'chats',
        });
      }

      if (talkItems.length > 0) {
        menuItems.push({
          id: '2talk',
          name: intl.formatMessage({ id: 'menu.2talk' }),
          icon: QuestionAnswer,
          subtitle: 'Chat e comunicação',
          items: talkItems
        });
      }
    }

    // Organizações
    if (this.isAdmGlobal || this.isAdm) {
      menuItems.push({
        id: 'organizations',
        name: intl.formatMessage({ id: 'menu.organizations' }),
        icon: AccountBalance,
        subtitle: 'Gestão organizacional',
        items: [
          {
            name: intl.formatMessage({ id: 'menu.organization' }),
            link: `/${this.props.match.params.lang}/organizations`,
            state: 'organizations',
          }
        ]
      });
    }

    // Acesso (Users)
    if (this.isAdm || this.isAdmGlobal) {
      const accessItems = [];

      if (this.context.can('List', 'User')) {
        accessItems.push({
          name: intl.formatMessage({ id: 'menu.users-agents' }),
          link: `/${this.props.match.params.lang}/users`,
          state: 'users',
        });
      }

      if (accessItems.length > 0) {
        menuItems.push({
          id: 'access',
          name: intl.formatMessage({ id: 'menu.access' }),
          icon: GroupAdd,
          subtitle: 'Usuários e permissões',
          items: accessItems
        });
      }
    }

    // Relatórios
    const reportItems = [];

    if (this.context.can('List', 'CalledReport')) {
      reportItems.push({
        name: intl.formatMessage({ id: 'called.report.header' }),
        link: `/${this.props.match.params.lang}/calledreports`,
        state: 'calledreports',
      });
    }

    if (reportItems.length > 0) {
      menuItems.push({
        id: 'reports',
        name: intl.formatMessage({ id: 'reports' }),
        icon: Assignment,
        subtitle: 'Relatórios e análises',
        items: reportItems
      });
    }

    // Configurações
    if (this.context.can('List', 'WorkHour')) {
      menuItems.push({
        id: 'configs',
        name: intl.formatMessage({ id: 'menu.configs' }),
        icon: SettingsApplications,
        subtitle: 'Configurações do sistema',
        items: [
          {
            name: intl.formatMessage({ id: 'default.actions' }),
            link: `/${this.props.match.params.lang}/default-actions`,
            state: 'defaultactions',
          }
        ]
      });
    }

    this.setState({ menuItems });
  };

  handleSearch = (event) => {
    this.setState({ searchTerm: event.target.value });
  };

  handleCategoryClick = (category) => {
    this.setState({
      selectedCategory: category,
      filteredSubitems: category.items || []
    });
  };

  handleSubitemClick = (item) => {
    // Navigate to the route
    this.props.history.push(item.link);
  };

  getFilteredCategories = () => {
    const { searchTerm, menuItems } = this.state;
    if (!searchTerm) return menuItems;

    return menuItems.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.items?.some(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  render() {
    const { classes } = this.props;
    const { searchTerm, selectedCategory, filteredSubitems } = this.state;
    const filteredCategories = this.getFilteredCategories();

    const theme = createTheme({
      palette: {
        primary: {
          main: '#8BC34A'
        }
      }
    });

    return (
      <Base>
        <ThemeProvider theme={theme}>
          <Box className={classes.root}>
            {/* Header */}
            <Box className={classes.header}>
              <Typography variant="h3" component="h1">
                <FormattedMessage id="menu.modern.title" defaultMessage="Menu Principal" />
              </Typography>
              <Typography variant="body1">
                <FormattedMessage id="menu.modern.subtitle" defaultMessage="Acesse todas as funcionalidades do sistema de forma organizada" />
              </Typography>
            </Box>

            {/* Search */}
            <Box className={classes.searchContainer}>
              <TextField
                className={classes.searchField}
                placeholder="Buscar funcionalidade..."
                variant="outlined"
                value={searchTerm}
                onChange={this.handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Main Content */}
            <Box className={classes.contentContainer}>
              {/* Categories Grid */}
              <Box className={classes.categoriesGrid}>
                <Grid container spacing={3}>
                  {filteredCategories.map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={category.id}>
                      <Card
                        className={`${classes.categoryCard} ${selectedCategory?.id === category.id ? 'selected' : ''}`}
                        onClick={() => this.handleCategoryClick(category)}
                      >
                        <CardContent className={classes.categoryContent}>
                          <category.icon className={classes.categoryIcon} />
                          <Typography variant="h6" className={classes.categoryTitle}>
                            {category.name}
                          </Typography>
                          <Typography variant="body2" className={classes.categorySubtitle}>
                            {category.subtitle}
                          </Typography>
                          <Chip
                            label={`${category.items?.length || 0} itens`}
                            size="small"
                            className={classes.itemCount}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Right Panel */}
              <Box className={classes.rightPanel}>
                {selectedCategory ? (
                  <>
                    <Box className={classes.panelHeader}>
                      <Typography variant="h6" className={classes.panelTitle}>
                        {selectedCategory.name}
                      </Typography>
                      <Typography variant="body2" className={classes.panelSubtitle}>
                        {selectedCategory.subtitle}
                      </Typography>
                    </Box>
                    <List className={classes.subitemsList}>
                      {filteredSubitems.map((item, index) => (
                        <Fade in={true} timeout={300 + (index * 100)} key={index}>
                          <ListItem
                            className={classes.subitemListItem}
                            onClick={() => this.handleSubitemClick(item)}
                          >
                            <ListItemIcon className={classes.subitemIcon}>
                              <QueuePlayNext />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              className={classes.subitemText}
                            />
                          </ListItem>
                        </Fade>
                      ))}
                    </List>
                  </>
                ) : (
                  <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                      Selecione uma categoria
                    </Typography>
                    <Typography variant="body2">
                      Clique em uma das categorias ao lado para ver suas opções
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      </Base>
    );
  }
}

const mapStateToProps = state => ({
  userSession: state.userSession,
});

export default withRouter(connect(mapStateToProps)(withStyles(styles)(injectIntl(ModernMenu))));